import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Movie } from '../../types/movie';
import styles from './RecommendCarousel.module.css';
import DescriptionModal from '../common/DescriptionModal';

interface RecommendCarouselProps {
  items: Movie[];
  title: string;
}

// 错误边界组件
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Carousel error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div style={{textAlign: 'center', padding: '20px'}}>加载内容出错，请刷新页面</div>;
    }
    return this.props.children;
  }
}

const RecommendCarousel: React.FC<RecommendCarouselProps> = ({ items, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [itemsPerView, setItemsPerView] = useState(5); // 默认PC端显示5个
  const carouselRef = useRef<HTMLDivElement>(null);

  // 根据屏幕宽度调整每屏展示的卡片数量
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 576) {
        setItemsPerView(2); // 移动端每屏显示2个
      } else if (window.innerWidth <= 768) {
        setItemsPerView(3); // 小平板端每屏显示3个
      } else if (window.innerWidth <= 1024) {
        setItemsPerView(4); // 平板端每屏显示4个
      } else {
        setItemsPerView(5); // PC端每屏显示5个
      }
    };

    handleResize(); // 初始化
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 确保当前索引不会超出范围
  useEffect(() => {
    const maxIndex = Math.max(0, items.length - itemsPerView);
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [itemsPerView, items.length, currentIndex]);

  const maxIndex = Math.max(0, items.length - itemsPerView);

  const handlePrevClick = () => {
    setCurrentIndex((prevIndex) => Math.max(0, prevIndex - itemsPerView));
  };

  const handleNextClick = () => {
    setCurrentIndex((prevIndex) => Math.min(maxIndex, prevIndex + itemsPerView));
  };

  const openModal = (movie: Movie, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // 导航到搜索页面
  const navigateToGenre = (e: React.MouseEvent, genre: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `/search?genre=${encodeURIComponent(genre)}`;
  };

  // 截断描述文本
  const truncateDescription = (text: string, maxLength = 70) => {
    if (!text) {return "暂无描述";}
    if (text.length <= maxLength) {return text;}
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <ErrorBoundary>
      <div className={styles.carouselContainer}>
        <div className={styles.carouselHeader}>
          <h2 className={styles.carouselTitle}>{title}</h2>
        </div>

        <div className={styles.carouselOuterWrapper}>
          <button 
            className={`${styles.carouselButton} ${styles.prevButton} ${currentIndex === 0 ? styles.disabled : ''}`} 
            onClick={handlePrevClick}
            disabled={currentIndex === 0}
            aria-label="Previous"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <div className={styles.carouselWrapper} ref={carouselRef}>
            <motion.div 
              className={styles.carouselTrack}
              animate={{ 
                x: -currentIndex * (100 / itemsPerView) + '%' 
              }}
              transition={{ 
                duration: 0.5,
                ease: "easeInOut",
                // 移除可能导致问题的spring动画
              }}
            >
              {items.map((movie) => {
                // 将 freeEpisodes 转换为数字
                const freeEpisodesCount = typeof movie.freeEpisodes === 'string' 
                  ? parseInt(movie.freeEpisodes, 10) 
                  : (movie.freeEpisodes || 0);
                
                // 判断是否为短剧
                const isDrama = movie.episodes > 1;
                
                return (
                  <div 
                    key={movie._id} 
                    className={styles.carouselItem}
                  >
                    <div className={styles.carouselCard}>
                      <a href={`details?id=${movie._id}&episode=1`} className={styles.cardLink}>
                        <div className={styles.imageContainer}>
                          <img 
                            src={movie.coverImageW} 
                            alt={movie.title} 
                            className={styles.image}
                            loading="lazy"
                          />
                          <div className={styles.overlay}>
                            <div className={styles.playButton}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 3L19 12L5 21V3Z" fill="white"/>
                              </svg>
                            </div>
                          </div>
                          
                          {/* 只为短剧显示genres，放置在图片区域左上角 */}
                          {isDrama && movie.genres && movie.genres.length > 0 && (
                            <div className={styles.imageGenres}>
                              {movie.genres.map((genre, i) => (
                                <span 
                                  key={i} 
                                  onClick={(e) => navigateToGenre(e, genre)}
                                  className={styles.genre}
                                >
                                  {genre}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </a>
                      
                      <div className={styles.cardInfo}>
                        <a href={`details?id=${movie._id}&episode=1`} className={styles.titleLink}>
                          <h3 className={styles.title}>{movie.title}</h3>
                        </a>
                        
                        {/* 为短剧添加描述，并使描述可点击跳转 */}
                        {isDrama && movie.description && (
                          <div className={styles.descriptionContainer}>
                            <a href={`details?id=${movie._id}&episode=1`} className={styles.descriptionLink}>
                              <p className={styles.description}>
                                {truncateDescription(movie.description)}
                              </p>
                            </a>
                          </div>
                        )}
                        
                        <div className={styles.meta}>
                          <span className={styles.year}>{movie.year}</span>
                          <span className={styles.separator}>•</span>
                          <span className={styles.duration}>{movie.duration}</span>
                          {movie.episodes > 1 && (
                            <>
                              <span className={styles.separator}>•</span>
                              <span className={styles.episodes}>{movie.episodes} EP</span>
                            </>
                          )}
                        </div>
                        
                        {/* 免费集数和分数与More details按钮并排展示 */}
                        <div className={styles.infoFooter}>
                          <div className={styles.badgesContainer}>
                            {freeEpisodesCount > 0 && (
                              <span className={styles.freeBadgeText}>
                                {freeEpisodesCount} Free
                              </span>
                            )}
                            <span className={styles.scoreBadgeText}>
                              {movie.score}
                            </span>
                          </div>
                          
                          {isDrama && movie.description && (
                            <button 
                              className={styles.moreDetailsButton} 
                              onClick={(e) => openModal(movie, e)}
                            >
                              More details
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>

          <button 
            className={`${styles.carouselButton} ${styles.nextButton} ${currentIndex >= maxIndex ? styles.disabled : ''}`} 
            onClick={handleNextClick}
            disabled={currentIndex >= maxIndex}
            aria-label="Next"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <DescriptionModal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          movie={selectedMovie} 
        />
      </div>
    </ErrorBoundary>
  );
};

export default RecommendCarousel; 